import Promise from 'bluebird'
import _ from 'lodash'
import React from 'react'

import { Entity } from 'shared-libs/models/entity'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { EntityFooter } from 'browser/components/atomic-elements/atoms/footer/entity-footer'
import { Sheet } from 'browser/components/atomic-elements/atoms/sheet'
import { EntityFormBlock } from './entity-form-block'
import { AJVSchemaValidator } from 'shared-libs/components/entity/ajv-validator'
import { creationSourceFromWeb } from 'browser/app/utils/utils'
import { EntityHeader } from './entity-header/entity-header'
import {
  IFormFocusHandle,
  FormFocusHandleContext,
  FormFocusHandleSetter,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { FormFocusTargetProvider } from 'browser/components/ui2/behavior/form-focus-target-provider'

export enum EntityFormSheetMode {
  Create = 'create',
  Edit = 'edit',
  View = 'view'
}

interface IEntityFormSheetProps extends IBaseProps {
  entity: Entity
  defaultValue?: object
  entitySchema: object
  maxHeight?: number
  onCreate: (entity: any) => void
  onEdit: (entity: any) => void
  onSelect: (entity: any) => void
  size?: string
  uiSchemaPath?: string
  extraState: any
  mode: EntityFormSheetMode
}

interface IEntityFormSheetState {
  errors: object
  entity: Entity
  isSaving: boolean
  mode: EntityFormSheetMode
}

export class EntityFormSheet extends React.Component<IEntityFormSheetProps, IEntityFormSheetState> {
  public static defaultProps: Partial<IEntityFormSheetProps> = {
    size: 'sm',
    uiSchemaPath: 'uiSchema.web.entityCreationSheet',
    mode: EntityFormSheetMode.Create
  }

  private sheet: Sheet

  constructor(props) {
    super(props)
    const { defaultValue, entitySchema, mode } = props
    const entityStore = apis.getStore()
    let entity: Entity
    let modeState = mode
    if (props.entity) {
      entity = props.entity
    } else {
      if (mode !== EntityFormSheetMode.Create) {
        console.warn(`EntityFormSheet was rendered with mode '${mode}' but no entity. Defaulting to 'Create' mode.`);
      }
      entity = entityStore.createRecord(entitySchema, this.defaultWithCreationProps(defaultValue))
      modeState = EntityFormSheetMode.Create // Default to Create mode if no entity is provided, even if a different mode was specified in props
    }
    this.state = {
      mode: modeState,
      entity,
      errors: {},
      isSaving: false
    }
  }

  private focusHandleRef: { current: IFormFocusHandle | null } = { current: null }
  private setFocusHandle: FormFocusHandleSetter = (h) => { this.focusHandleRef.current = h }

  public render() {
    const { maxHeight, size, uiSchemaPath, extraState } = this.props
    const { entity, errors } = this.state
    const handleRef = (ref) => this.sheet = ref
    const settings = apis.getSettings()

    return (
      <FormFocusHandleContext.Provider value={this.setFocusHandle}>
        <FormFocusTargetProvider>
          <Sheet
            header={this.renderSheetHeader()}
            footer={this.renderSheetFooter()}
            maxHeight={maxHeight}
            ref={handleRef}
            size={size}
          >
            <EntityFormBlock
              entity={entity}
              errors={errors}
              onSave={this.handleSave}
              onSelect={this.handleSelect}
              uiSchemaPath={uiSchemaPath}
              settings={settings}
              state={extraState}
        />
          </Sheet>
        </FormFocusTargetProvider>
      </FormFocusHandleContext.Provider>
    )
  }

  private handleSelect = (entity) => {
    this.props.onSelect(entity)
    this.sheet.close()
  }

  private runValidations = async (entity: Entity, customValidations: any) => {
    const customValidator = customValidations
      ? await AJVSchemaValidator.compile(customValidations)
      : null

    const errors = await entity.validate(true, customValidator)

    if (_.isEmpty(errors)) {
      return
    }

    this.setState({
      errors
    })
    void this.focusHandleRef.current?.focusFirstError()

    throw new Error("Failed to validate entity before save")
  }

  private defaultWithCreationProps(defaultValue: any) {
    return _.merge({ creationSource: creationSourceFromWeb('Entity Form Sheet') }, defaultValue)
  }

  private handleSave = (entity): Promise<any> => {
    const { onCreate, onEdit, uiSchemaPath } = this.props
    const { mode } = this.state

    const validationSchema = entity.resolveSubschemaByPath(uiSchemaPath)?.schema?.validationSchema
    const validationPromise = new Promise((resolve, reject) => {
      if (validationSchema) {
        this.runValidations(entity, validationSchema)
          .then(() => resolve(entity))
          .catch(reject)
      } else {
        resolve(entity)
      }
    })

    return validationPromise.then((errors: any) => {
      this.setState({ isSaving: true })
      return entity.save().then(() => {
        if (mode === EntityFormSheetMode.Edit) {
          onEdit?.(entity)
        } else if (mode === EntityFormSheetMode.Create) {
          onCreate?.(entity)
        }
        this.sheet.close()
      }).catch(response => {
        const errors = _.get(response, 'errors')

        if (_.isNil(errors)) {
          return
        }

        this.setState({
          errors
        })
        void this.focusHandleRef.current?.focusFirstError()
      })
    })
  }

  private handleCancel = (entity) => {
    entity?.rollback()
    this.sheet.close()
  }

  private renderSheetHeader() {
    const { entity, mode } = this.state

    let title = "Viewing " + entity.displayName
    if (mode === EntityFormSheetMode.Edit) {
      title = "Editing " + entity.displayName
    } else if (mode === EntityFormSheetMode.Create) {
      title = "Creating " + entity.entityType
    }

    return (
      <EntityHeader
        showOpenInNewTabButton={!_.isNil(entity)}
        entity={entity}
        className='u-borderBottom'
      >
        <div className={'c-cardHeader-item'}>
          <h3 className='c-cardHeader-title'>
            {title}
          </h3>
          <div className='c-cardHeader-label'>
            {entity.entityType}
          </div>
        </div>
      </EntityHeader>
    )
  }

  private renderSheetFooter() {
    const { entity, errors, isSaving, mode } = this.state
    const onConfirm = () => this.handleSave(entity)
    const onCancel = () => this.handleCancel(entity)
    return (
      <EntityFooter
        entity={entity}
        errors={errors}
        isPrimaryButtonLoading={isSaving}
        isVisible={true}
        onCancelButtonClick={onCancel}
        onPrimaryButtonClick={mode !== EntityFormSheetMode.View ? onConfirm : undefined}
        cancelButtonText={mode === EntityFormSheetMode.View ? "Close" : "Cancel"}
      />
    )
  }
}
